import React, { useContext, useMemo, useState } from "react";
import {
  PageHeader,
  Icons,
  IconButton,
  Button,
  DataTable,
  useClientTable,
  Text,
  Menu,
} from "@pushpress/shared-components";
import { EmptyState } from "@pushpress/pushpress-ui";
import { useToggle } from "../../../hooks/state";
import { openWindow } from "../../../utils/browserUtils";
import { AppointmentPackage } from "../../../apis/appointmentApi";
import { useAppointmentPackagesQuery } from "../../../apis/appointmentApi.hooks";
import ColorSquare from "../../../components/common/ColorSquare/ColorSquare";
import PageContainer from "../../../components/common/PageContainer/PageContainer";
import { AppContext } from "../../../context/app";
import { formatToUSD } from "../../../utils/misc";
import AppointmentPackageDrawer from "../AppointmentPackageDrawer";
import DeleteModal from "./AppointmentPackageDeleteModal";
import LandingInfoModal from "./AppointmentLandingInfoModal";
import PackageIcon from "../../../components/icons/PackageIcon";

function AppointmentPackageList() {
  const app = useContext(AppContext);
  const [drawerVisible, drawerActions] = useToggle(false);
  const [editData, setEditData] = useState<AppointmentPackage>();
  const [duplicate, duplicateActions] = useToggle(false);
  const [deleteVisible, deleteActions] = useToggle(false);
  const [landingPageVisible, landingPageActions] = useToggle(false);
  const appointmentPackagesQuery = useAppointmentPackagesQuery();

  const columns = useMemo(
    () => [
      {
        key: "name",
        header: "Name",
        width: "25%",
        widthMobile: "auto",
        isSortable: true,
      },
      {
        key: "type",
        header: "Type",
        width: "25%",
        widthMobile: "auto",
        isSortable: true,
        hideOnMobile: true,
        sort: (a: AppointmentPackage, b: AppointmentPackage) =>
          a.appointmentType.name.localeCompare(b.appointmentType.name),
        cell: (row: AppointmentPackage) => {
          // This console log is just to make sure we have this code on prod, will be removed later.
          console.log(row?.appointmentType?.name);
          return (
            <>
              <ColorSquare color={row.appointmentType.color} className="mr-2" />
              <Text variant="body-lg" component="span">
                {row?.appointmentType?.name || "--"}
              </Text>
            </>
          );
        },
      },
      {
        key: "numberOfSessions",
        header: "Credits",
        width: "25%",
        isSortable: true,
        hideOnMobile: true,
      },
      {
        key: "packagePrice",
        header: "Price",
        width: "25%",
        isSortable: true,
        hideOnMobile: true,
        cell: (row: AppointmentPackage) => {
          const total =
            row.packagePrice > 0
              ? formatToUSD(row.packagePrice, app.client.currencyIso)
              : "Free";
          return <Text variant="body-lg">{total}</Text>;
        },
      },
    ],
    [app.client.currencyIso],
  );

  const { sort, data, handleSort } = useClientTable({
    data: appointmentPackagesQuery?.data?.data || [],
    columns,
    defaultSort: { key: "name", order: "asc" },
  });

  const handleLandingPageClick = (data: AppointmentPackage) => {
    setEditData(data);
    duplicateActions.off();
    landingPageActions.on();
  };

  const handleEditClick = (data: AppointmentPackage) => {
    setEditData(data);
    duplicateActions.off();
    drawerActions.on();
  };

  const handleDuplicateClick = (data: AppointmentPackage) => {
    setEditData(data);
    duplicateActions.on();
    drawerActions.on();
  };

  const handleDeleteClick = (data: AppointmentPackage) => {
    setEditData(data);
    deleteActions.on();
  };

  const handleAddClick = () => {
    setEditData(undefined);
    duplicateActions.off();
    drawerActions.on();
  };

  const handleDrawerClose = () => {
    setEditData(undefined);
    drawerActions.off();
  };

  const handleSuccess = () => {
    setEditData(undefined);
    drawerActions.off();
    appointmentPackagesQuery.refetch();
  };

  const handleHelp = () => {
    openWindow(
      "https://help.pushpress.com/en/articles/6332470-appointments-beta-creating-and-using-packages",
    );
  };

  const getRowId = (row: AppointmentPackage) => {
    return row.uuid;
  };

  const renderRowMenu = (row: AppointmentPackage, closeMenu: VoidFunction) => {
    return (
      <Menu>
        <Menu.Item
          label="Landing pages"
          action={() => {
            handleLandingPageClick(row);
            closeMenu();
          }}
        ></Menu.Item>
        <Menu.Item
          label="Edit"
          action={() => {
            handleEditClick(row);
            closeMenu();
          }}
        ></Menu.Item>
        <Menu.Item
          label="Duplicate"
          action={() => {
            handleDuplicateClick(row);
            closeMenu();
          }}
        ></Menu.Item>
        <Menu.Item
          label="Delete"
          action={() => {
            handleDeleteClick(row);
            closeMenu();
          }}
        ></Menu.Item>
      </Menu>
    );
  };

  const emptyState = (
    <EmptyState
      icon={<PackageIcon />}
      title="No packages found"
      description="Create a package to offer your clients a set number of sessions at a fixed price."
      primaryActionLabel="Add Package"
      primaryActionVariant="dark"
      primaryActionOnClick={handleAddClick}
      learnMoreHref="https://help.pushpress.com/en/articles/6332470-core-appointments-creating-and-using-packages"
      learnMoreLabel="Learn more"
    />
  );
  return (
    <div className="tw-flex tw-flex-col md:tw-px-8 md:tw-pt-4 tw-w-full tw-mx-auto tw-max-w-7xl">
      <PageContainer>
        <PageHeader title="Appointment Packages">
          <PageHeader.ActionMenu>
            <IconButton
              icon={Icons.Help}
              tooltip="Learn more"
              onClick={handleHelp}
            />
            <Button
              text="Package"
              size="large"
              iconLocation="left"
              hideTextOnMobile
              icon={Icons.Plus}
              iconBold
              onClick={handleAddClick}
            />
          </PageHeader.ActionMenu>
        </PageHeader>
        <DataTable
          data={data}
          columns={columns}
          sort={sort}
          loading={appointmentPackagesQuery.isLoading}
          getRowId={getRowId}
          onSort={handleSort}
          renderRowMenu={renderRowMenu}
          customEmptyState={emptyState}
        />
      </PageContainer>
      <AppointmentPackageDrawer
        visible={drawerVisible}
        duplicate={duplicate}
        appointmentPackageUuid={editData?.uuid}
        onClose={handleDrawerClose}
        onSuccess={handleSuccess}
      />
      <DeleteModal
        visible={deleteVisible}
        appointmentPackageUuid={editData?.uuid}
        onClose={deleteActions.off}
        onSuccess={handleSuccess}
      />
      <LandingInfoModal
        visible={landingPageVisible}
        appointmentPackage={editData}
        onClose={landingPageActions.off}
      />
    </div>
  );
}

export default AppointmentPackageList;
